import type { ComputedRef } from 'vue'
import type { RevalidationItem } from '~/store-v2/revalidations/revalidationList.types'
import { normalizeBrandKey } from '~/ui-blocks/Revalidations/revalidations.constants'

export interface UseRevalidationSelectionOptions {
  itemsByKey: ComputedRef<Map<string, RevalidationItem>>
  batchSelectionBlocked: ComputedRef<boolean>
  getEffectiveBrand?: (rowKey: string, item: RevalidationItem) => string
}

export function useRevalidationSelection(options: UseRevalidationSelectionOptions) {
  const { itemsByKey, batchSelectionBlocked, getEffectiveBrand } = options

  function effectiveBrand(rowKey: string, item: RevalidationItem): string {
    return getEffectiveBrand
      ? getEffectiveBrand(rowKey, item)
      : normalizeBrandKey(item.reference_inverter_brand)
  }

  const selectedItems = ref<Set<string>>(new Set())

  watch(batchSelectionBlocked, (blocked) => {
    if (blocked)
      selectedItems.value = new Set()
  })

  const selectionLockedBrand = computed((): string | null => {
    if (selectedItems.value.size === 0)
      return null
    for (const [key, item] of itemsByKey.value) {
      if (selectedItems.value.has(key))
        return effectiveBrand(key, item)
    }
    return null
  })

  const selectableRowKeys = computed(() => {
    const lock = selectionLockedBrand.value
    if (lock == null)
      return [...itemsByKey.value.keys()]
    return [...itemsByKey.value.entries()]
      .filter(([rowKey, item]) => effectiveBrand(rowKey, item) === lock)
      .map(([rowKey]) => rowKey)
  })

  const allSelected = computed(() => {
    const keys = selectableRowKeys.value
    if (keys.length === 0) return false
    const sel = selectedItems.value
    return keys.every(k => sel.has(k))
  })

  const someSelected = computed(() => {
    if (allSelected.value) return false
    const sel = selectedItems.value
    return selectableRowKeys.value.some(k => sel.has(k))
  })

  const rowCheckboxDisabledMap = computed(() => {
    const map = new Map<string, boolean>()
    const blocked = batchSelectionBlocked.value
    const lock = selectionLockedBrand.value
    const sel = selectedItems.value

    if (blocked) {
      for (const rowKey of itemsByKey.value.keys())
        map.set(rowKey, true)
      return map
    }

    for (const [rowKey, item] of itemsByKey.value) {
      if (sel.has(rowKey)) {
        map.set(rowKey, false)
        continue
      }
      if (lock == null) {
        map.set(rowKey, false)
        continue
      }
      map.set(rowKey, effectiveBrand(rowKey, item) !== lock)
    }
    return map
  })

  function toggleSelectAll() {
    if (batchSelectionBlocked.value)
      return
    const keys = selectableRowKeys.value
    selectedItems.value = allSelected.value ? new Set() : new Set(keys)
  }

  function toggleItem(key: string) {
    if (batchSelectionBlocked.value)
      return
    const next = new Set(selectedItems.value)
    if (next.has(key)) {
      next.delete(key)
      selectedItems.value = next
      return
    }
    const item = itemsByKey.value.get(key)
    if (!item)
      return
    const lock = selectionLockedBrand.value
    if (lock != null && effectiveBrand(key, item) !== lock)
      return
    next.add(key)
    selectedItems.value = next
  }

  function selectFilledByPredicate(isItemFilled: (rowKey: string, defaultPlantName: string) => boolean) {
    if (batchSelectionBlocked.value)
      return
    const lock = selectionLockedBrand.value
    selectedItems.value = new Set(
      [...itemsByKey.value.entries()]
        .map(([rowKey, item]) => {
          if (lock != null && effectiveBrand(rowKey, item) !== lock)
            return null
          const defaultPlant = (item.plant_name?.trim()) ? item.plant_name.trim() : '—'
          return isItemFilled(rowKey, defaultPlant) ? rowKey : null
        })
        .filter((k): k is string => k !== null),
    )
  }

  function selectAll() {
    if (batchSelectionBlocked.value)
      return
    selectedItems.value = new Set(selectableRowKeys.value)
  }

  function selectNone() {
    selectedItems.value = new Set()
  }

  return {
    selectedItems,
    selectionLockedBrand,
    selectableRowKeys,
    allSelected,
    someSelected,
    rowCheckboxDisabledMap,
    toggleItem,
    toggleSelectAll,
    selectAll,
    selectNone,
    selectFilledByPredicate,
  }
}
